import { currentUserId } from '@/lib/session'
import { supabase } from '@/lib/supabase'

/* RLS does the authorization. `picks_insert_own_before_lock` and
   `picks_update_own_before_lock` already enforce user_id = auth.uid(),
   is_pool_member(pool_id) and not game_week_is_locked(game_id), so there is no
   lock check and no membership check in this file. A client-side copy of those
   rules drifts from the policy the moment either changes, and the client copy
   is the one nobody updates.

   A write attempted after the week locks comes back as a Postgres error, not a
   silent no-op. It throws. Do not swallow it — the caller needs to know the
   pick did not land.

   There is deliberately no delete function: `picks` has no DELETE policy. Picks
   are permanent once submitted and edits go through the upsert below. */

export async function fetchMyPicks(poolId: string, weekId: string) {
  const userId = await currentUserId()

  // The user_id filter is not a permission check — it is what makes this "my"
  // picks. `picks_select_pool_after_lock` legitimately exposes every member's
  // picks once the week locks, so without this predicate the same query starts
  // returning the whole pool's rows the moment the first game kicks off.
  //
  // picks has no week_id; it reaches weeks through games. The !inner join is
  // what scopes to the week, and it carries week_id back so the caller can
  // match rows against a games query keyed the same way.
  const { data, error } = await supabase
    .from('picks')
    .select('id, pool_id, user_id, game_id, picked_team, is_lock, games!inner(week_id)')
    .eq('pool_id', poolId)
    .eq('user_id', userId)
    .eq('games.week_id', weekId)
  if (error) throw error
  return data
}

/* Flips is_lock on a pick that already exists.
   An update rather than an upsert: the toggle is only reachable on a row that
   has been picked, so there is nothing to insert, and updating means the call
   does not need to restate picked_team and cannot accidentally change it.

   After the week locks this fails at picks_update_own_before_lock, which is
   correct — the UI disables the toggle, and the database does not rely on it. */
async function setPickLock(poolId: string, gameId: string, isLock: boolean) {
  const userId = await currentUserId()

  const { data, error } = await supabase
    .from('picks')
    .update({ is_lock: isLock })
    .eq('pool_id', poolId)
    .eq('user_id', userId)
    .eq('game_id', gameId)
    .select()
    .single()
  if (error) throw error
  return data
}

/* Moves the week's Lock to a different game, or clears it.
   ORDER IS LOAD-BEARING. `trg_picks_one_lock_per_week` raises
   'User already has a Lock of the Week for this pool/week' the moment a second
   is_lock=true exists for the same pool, user and week, so the old lock has to
   be cleared BEFORE the new one is set. Setting first always fails.

   Two statements, not one transaction — supabase-js has no client-side
   transaction. The failure mode is therefore "cleared the old lock, failed to
   set the new one", which leaves the member with no lock rather than two. That
   is the right way round: no lock is a state they can fix from the same button,
   whereas two locks is a state the database will not hold at all.

   Every write here lands in picks_audit via trg_picks_audit, so a lock moved
   late in a week is on the record like any other pick change. */
export async function setWeekLock(args: {
  poolId: string
  gameId: string
  currentLockGameId: string | null
  enabled: boolean
}) {
  if (!args.enabled) {
    return setPickLock(args.poolId, args.gameId, false)
  }

  if (args.currentLockGameId && args.currentLockGameId !== args.gameId) {
    await setPickLock(args.poolId, args.currentLockGameId, false)
  }

  return setPickLock(args.poolId, args.gameId, true)
}

export async function upsertPick(pick: {
  poolId: string
  gameId: string
  pickedTeam: string
  // Upsert writes the whole row, so an omitted is_lock would clear an existing
  // Lock of the Week as a side effect of changing which team was picked. The
  // caller passes the value it wants to survive, not the value it is changing.
  isLock?: boolean
}) {
  const userId = await currentUserId()

  const { data, error } = await supabase
    .from('picks')
    .upsert(
      {
        pool_id: pick.poolId,
        user_id: userId,
        game_id: pick.gameId,
        picked_team: pick.pickedTeam,
        is_lock: pick.isLock ?? false,
      },
      // Matches `unique (pool_id, user_id, game_id)` on public.picks. Conflict
      // on that key is the edit path; anything else is a genuine error.
      { onConflict: 'pool_id,user_id,game_id' }
    )
    .select()
    .single()
  if (error) throw error
  return data
}
